import { useState, useRef, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Play, Pause, Volume2, VolumeX, AlertCircle, Music2, User } from 'lucide-react'
import { shares, type SharedTrackInfo } from '../api/client'

export default function SharedTrack() {
  const { token } = useParams<{ token: string }>()
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolume] = useState(1)
  const [isMuted, setIsMuted] = useState(false)
  const audioRef = useRef<HTMLAudioElement>(null)

  const { data: shareInfo, isLoading, error } = useQuery<SharedTrackInfo>({
    queryKey: ['shared-track', token],
    queryFn: () => shares.getSharedTrack(token!),
    enabled: !!token,
    retry: false,
  })

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime)
    const handleLoadedMetadata = () => setDuration(audio.duration)
    const handleEnded = () => setIsPlaying(false)

    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('ended', handleEnded)

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('ended', handleEnded)
    }
  }, [shareInfo])

  const togglePlayPause = () => {
    const audio = audioRef.current
    if (!audio) return

    if (isPlaying) {
      audio.pause()
    } else {
      audio.play()
    }
    setIsPlaying(!isPlaying)
  }

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current
    if (!audio) return
    const time = parseFloat(e.target.value)
    audio.currentTime = time
    setCurrentTime(time)
  }

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current
    if (!audio) return
    const vol = parseFloat(e.target.value)
    audio.volume = vol
    setVolume(vol)
    setIsMuted(vol === 0)
  }

  const toggleMute = () => {
    const audio = audioRef.current
    if (!audio) return
    if (isMuted) {
      audio.volume = volume || 1
      setIsMuted(false)
    } else {
      audio.volume = 0
      setIsMuted(true)
    }
  }

  const formatTime = (seconds: number) => {
    if (isNaN(seconds)) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    )
  }

  if (error || !shareInfo) {
    const errorMessage = error instanceof Error ? error.message : 'This share link is no longer available'
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-xl font-semibold text-white mb-2">Share Not Available</h1>
          <p className="text-dark-400">{errorMessage}</p>
        </div>
      </div>
    )
  }

  const track = shareInfo.track
  const albumArt = track.cover_path
    ? `/api/image/${encodeURIComponent(track.cover_path)}`
    : null

  return (
    <div className="min-h-screen bg-dark-900 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-dark-800 rounded-2xl shadow-2xl overflow-hidden">
          {/* Album Art */}
          <div className="aspect-square bg-dark-700 relative">
            {albumArt ? (
              <img
                src={albumArt}
                alt={track.album_title || 'Album art'}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Music2 className="w-24 h-24 text-dark-500" />
              </div>
            )}
          </div>

          {/* Track Info & Controls */}
          <div className="p-6">
            {/* Track Title & Artist */}
            <div className="text-center mb-6">
              <h1 className="text-xl font-semibold text-white truncate mb-1">
                {track.title || 'Unknown Track'}
              </h1>
              <p className="text-dark-400 truncate">
                {track.artist_name || 'Unknown Artist'}
              </p>
              {track.album_title && (
                <p className="text-dark-500 text-sm truncate mt-1">
                  {track.album_title}
                </p>
              )}
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <input
                type="range"
                min={0}
                max={duration || 100}
                value={currentTime}
                onChange={handleSeek}
                className="w-full h-1 bg-dark-600 rounded-lg appearance-none cursor-pointer accent-primary-500"
              />
              <div className="flex justify-between text-xs text-dark-400 mt-1">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Play Controls */}
            <div className="flex items-center justify-center gap-4 mb-6">
              <button
                onClick={togglePlayPause}
                className="w-14 h-14 bg-primary-500 hover:bg-primary-600 rounded-full flex items-center justify-center transition-colors"
              >
                {isPlaying ? (
                  <Pause className="w-6 h-6 text-white" fill="white" />
                ) : (
                  <Play className="w-6 h-6 text-white ml-1" fill="white" />
                )}
              </button>
            </div>

            {/* Volume Control */}
            <div className="flex items-center gap-3">
              <button
                onClick={toggleMute}
                className="text-dark-400 hover:text-white transition-colors"
              >
                {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
              </button>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                className="flex-1 h-1 bg-dark-600 rounded-lg appearance-none cursor-pointer accent-primary-500"
              />
            </div>

            {/* Share Info */}
            <div className="mt-6 pt-4 border-t border-dark-700">
              <div className="flex items-center justify-center gap-2 text-sm text-dark-400">
                <User size={14} />
                <span>Shared by {shareInfo.shared_by_username}</span>
              </div>
              {shareInfo.expires_at && (
                <p className="text-center text-xs text-dark-500 mt-2">
                  Expires: {new Date(shareInfo.expires_at).toLocaleString()}
                </p>
              )}
              {shareInfo.max_plays && (
                <p className="text-center text-xs text-dark-500 mt-1">
                  Plays: {shareInfo.play_count} / {shareInfo.max_plays}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Branding */}
        <p className="text-center text-dark-500 text-xs mt-4">
          Powered by S3ND Media
        </p>
      </div>

      {/* Hidden Audio Element */}
      {shareInfo && (
        <audio
          ref={audioRef}
          src={shares.getStreamUrl(token!, true)}
          preload="metadata"
        />
      )}
    </div>
  )
}
